import { Hand } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MeetingParticipant } from '@hub/contracts'
import { Button } from '@/components/ui/button'

/**
 * The host's list of raised hands (board m5gc #12): who asked to speak, in
 * the order they asked, with a "lower" action per person. Everyone else
 * sees hands only as badges on the tiles.
 */
export function RaisedHandsList({
  participants,
  selfUserId,
  rightOffset = 12,
  onLower,
}: {
  participants: MeetingParticipant[]
  selfUserId: string | undefined
  /** Keeps the list off the live panel on desktop. */
  rightOffset?: number
  onLower: (userId: string) => void
}) {
  const { t } = useTranslation('meet')
  const raised = participants
    .filter((p) => p.handRaisedAt)
    .sort((a, b) => (a.handRaisedAt! < b.handRaisedAt! ? -1 : 1))
  if (raised.length === 0) return null
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="raised-hands"
      className="border-border bg-background/95 absolute top-3 z-30 w-64 max-w-[calc(100%-1.5rem)] rounded-xl border p-2 shadow-lg backdrop-blur"
      style={{ right: rightOffset }}
    >
      <p className="text-muted-foreground mb-1 px-1 text-xs font-semibold uppercase">
        {t('hands.title', { count: raised.length })}
      </p>
      <ul className="space-y-1">
        {raised.map((p) => (
          <li key={p.userId} className="flex items-center gap-2 px-1 text-sm">
            <Hand className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {t('hands.raised', { name: p.userId === selfUserId ? t('dock.you') : p.name })}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => onLower(p.userId)}
            >
              {t('hands.lower')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}
